import { mdiAutorenew, mdiCreationOutline, mdiRefresh } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { useAiExtensionConnectionStatus } from '../../../services/ai-adapter/connection-check';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { ConfigurationFieldError } from './configuration-action-summary';
import * as S from './configuration-rule.styles';

interface IConfigurationFunctionBuilderProps {
    disabled?: boolean;
    ruleId: string;
    store: WorkerStore;
}

/**
 * The AI half of the Execute Function action: describe the edit in plain language, AI writes the
 * function body into `ConfigurationFunctionEditor` below for review. Purely additive — the code
 * editor is always there and always hand-editable, with or without this. When the AI extension
 * isn't installed/connected, this whole block is skipped rather than shown disabled or behind a
 * modal (same choice `ai-summary-card.tsx` makes): manual authoring is the real fallback, not a
 * dead button.
 */
export const ConfigurationFunctionBuilder: React.FC<IConfigurationFunctionBuilderProps> = observer(
    ({ disabled, ruleId, store }) => {
        const { connected } = useAiExtensionConnectionStatus({ autoCheck: true });

        if (connected !== true) {
            return null;
        }

        const builder = store.getFunctionBuilder(ruleId);
        const canBuild = !disabled && !builder.building && builder.prompt.trim().length > 0;
        const hasCode = builder.mode === 'code';

        return (
            <S.FunctionBuilderShell>
                <div>
                    <S.FunctionBuilderHeader>
                        <S.FunctionBuilderIcon>
                            <Icon path={mdiCreationOutline} size={0.7} aria-hidden={true} />
                        </S.FunctionBuilderIcon>
                        <S.FunctionBuilderTitle>Edit the claim automatically</S.FunctionBuilderTitle>
                    </S.FunctionBuilderHeader>
                    <S.FunctionBuilderDesc>
                        Describe the edit in plain language and the AI will write the function below.
                        Review the code before saving — it runs on every claim this rule matches.
                    </S.FunctionBuilderDesc>
                </div>

                {hasCode ? (
                    <S.FunctionBuilderPromptDisplay>
                        <S.FunctionBuilderPromptText>{builder.prompt}</S.FunctionBuilderPromptText>
                        <S.FunctionBuilderEditPromptButton
                            type="button"
                            disabled={disabled}
                            onClick={() => store.editFunctionPrompt(ruleId)}>
                            <Icon path={mdiRefresh} size={0.6} aria-hidden={true} />
                            Change the prompt
                        </S.FunctionBuilderEditPromptButton>
                    </S.FunctionBuilderPromptDisplay>
                ) : (
                    <div>
                        <S.FieldTextarea
                            rows={3}
                            disabled={disabled || builder.building}
                            placeholder="e.g. On CO-97 denials, if there's an E/M line billed the same day as a procedure, append modifier 25 to the E/M line. Skip claims that already have modifier 25."
                            value={builder.prompt}
                            onChange={event => store.setFunctionPrompt(ruleId, event.target.value)}
                        />
                    </div>
                )}

                {builder.building ? (
                    <S.FunctionBuilderBuildingRow>
                        <S.FunctionBuilderBuildingIcon>
                            <Icon path={mdiAutorenew} size={0.85} aria-hidden={true} />
                        </S.FunctionBuilderBuildingIcon>
                        <S.FunctionBuilderBuildingText>
                            <S.FunctionBuilderBuildingTitle>Writing the function…</S.FunctionBuilderBuildingTitle>
                            <S.FunctionBuilderBuildingSubtitle>
                                This usually takes a few seconds.
                            </S.FunctionBuilderBuildingSubtitle>
                        </S.FunctionBuilderBuildingText>
                    </S.FunctionBuilderBuildingRow>
                ) : (
                    !hasCode && (
                        <S.FunctionBuilderBuildRow>
                            <S.FunctionBuilderBuildButton
                                type="button"
                                disabled={!canBuild}
                                onClick={() => void store.buildRuleFunction(ruleId)}>
                                <Icon path={mdiCreationOutline} size={0.7} aria-hidden={true} />
                                Build the function
                            </S.FunctionBuilderBuildButton>
                            <S.FunctionBuilderBuildHint>
                                Nothing runs on live claims until you save the rule.
                            </S.FunctionBuilderBuildHint>
                        </S.FunctionBuilderBuildRow>
                    )
                )}

                <ConfigurationFieldError message={builder.error} />
            </S.FunctionBuilderShell>
        );
    },
);
